import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Row } from "antd";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import entityType from "../../../api/entityType";
import { transformOnHandInput } from "../../../store/inventory/inventoryActions";
import {
  clearOperationResult,
  exportReport,
  fetchReport,
  resetReportState,
} from "../../../store/report/reportActions";
import PermissionWrapper from "../../navigation/PermissionWrapper";
import ReportPanel from "../../shared/ReportPanel";
import AgencySelectEx from "../../shared/form/AgencySelectEx";
import { getInitialAgency } from "../../shared/form/getInitialAgency";
import CustomCheckbox from "../../shared/form/reactHookForm/CustomCheckbox";
import openNotificationOperationResult from "../../shared/openNotificationOperationResult";
import { ViewInventory } from "../../shared/securityRoleTasks";
import { agenciesListEqual } from "../../shared/sorting";
import AllocatedToAgentSelect from "./AllocatedToAgentSelect";
import { onHandInventoryReportSchema } from "./reportSchemas";

// Constants
const partialInitialValues = {
  AgencyKey: "",
  AllocatedToAgentKey: null,
  IncludeAllocatedToAgent: false,
  GroupByAgent: false,
};

const reportName = "OnHandInventoryReport";

const OnHandInventoryReport = () => {
  // Form
  const {
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { isValid, isDirty, defaultValues, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: partialInitialValues,
    resolver: yupResolver(onHandInventoryReportSchema),
  });

  const agencyKey = watch("AgencyKey");
  const includeAllocatedToAgent = watch("IncludeAllocatedToAgent");
  const allocatedToAgentKey = watch("AllocatedToAgentKey");

  //
  const dispatch = useDispatch();

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [initialDefaultValues, setInitialDefaultValues] =
    useState(partialInitialValues);

  //
  const agencies = useSelector((state) => state.agency.listCompact);
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const isExporting = useSelector((state) => state.report.isExporting);
  const reportOperationResult = useSelector(
    (state) => state.report.operationResult
  );

  //
  useEffect(() => {
    if (
      agencies != null &&
      !isEmpty(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      var initialAgency = getInitialAgency(homeAgency, agencies);
      const fullInitialValues = {
        ...partialInitialValues,
        AgencyKey: initialAgency,
      };
      setInitialDefaultValues(cloneDeep(fullInitialValues));

      reset(fullInitialValues);
      trigger();
    }
  }, [agencies, homeAgency]);

  useEffect(() => {
    openNotificationOperationResult(reportOperationResult, () =>
      dispatch(clearOperationResult())
    );
  }, [reportOperationResult, dispatch]);

  const onSubmitHandler = (values) => {
    dispatch(
      fetchReport(
        transformOnHandInput(values),
        entityType.INVENTORY,
        "PrintOnHand",
        () => {
          reset(values);
        }
      )
    );
  };

  const handlers = {
    onSubmit: () => handleSubmit(onSubmitHandler)(),
    onExport: () =>
      dispatch(
        exportReport(
          transformOnHandInput(getValues()),
          reportName,
          entityType.INVENTORY
        )
      ),
    onRestore: () => {
      reset(initialDefaultValues);
      dispatch(resetReportState());
    },
  };

  const buttons = [
    {
      action: "preview",
      disabled: !isValid || isExporting,
    },
    {
      action: "export",
      disabled: !isValid,
      onClick: handlers.onExport,
    },
    { action: "restore", onClick: handlers.onRestore },
  ];

  return (
    <PermissionWrapper entityName="InventoryReports">
      <ReportPanel
        isDirty={isDirty}
        isSubmitted={isSubmitted}
        onSubmit={handlers.onSubmit}
        buttons={buttons}
        taskId={ViewInventory}
      >
        <Row>
          <Col span={8}>
            <AgencySelectEx
              layout={{
                labelCol: {
                  span: 4,
                },
                wrapperCol: {
                  span: 19,
                },
              }}
              formLabel="Agency"
              name="AgencyKey"
              required={true}
              formStyle={{ marginBottom: 0 }}
              agencies={currentAgencies}
              isLoading={defaultValues.AgencyKey === ""}
              control={control}
              onSelect={(_) => {
                setValue("AllocatedToAgentKey", null);
                trigger();
              }}
            />
          </Col>
          <Col span={6}>
            <AllocatedToAgentSelect
              control={control}
              agencyKey={agencyKey}
              includeAllOption={true}
              allowClear={true}
              layout={{
                labelCol: {
                  span: 8,
                },
                wrapperCol: {
                  span: 15,
                },
              }}
              formStyle={{ marginBottom: 0 }}
              setValue={setValue}
            />
          </Col>
          <Col span={3}>
            {allocatedToAgentKey !== null && (
              <CustomCheckbox
                control={control}
                layout={{
                  labelCol: { span: 19 },
                  wrapperCol: { span: 4 },
                }}
                formLabel="Include Agent(s)"
                name="IncludeAllocatedToAgent"
                style={{ marginBottom: 0 }}
                onChange={(e) => {
                  if (e.target.checked === false) {
                    setValue("GroupByAgent", false);
                  }
                }}
              />
            )}
          </Col>
          <Col span={3}>
            {allocatedToAgentKey !== null && includeAllocatedToAgent && (
              <CustomCheckbox
                control={control}
                formLabel="Group By Agent"
                name="GroupByAgent"
                style={{ marginBottom: 0 }}
                disabled={!includeAllocatedToAgent}
              />
            )}
          </Col>
        </Row>
      </ReportPanel>
    </PermissionWrapper>
  );
};
export default OnHandInventoryReport;
